import { supabase } from '../supabase';
import { config } from '../config';
import type { Product, ProductWithStats, ProductInsert, ProductUpdate } from '../types';

/**
 * Get rating stats for a product
 * Queries the view directly using product_id
 */
const getProductStats = async (productId: string) => {
  const { data, error } = await supabase
    .from('product_rating_stats')
    .select('*')
    .eq('product_id', productId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching product stats:', error);
    return null;
  }
  return data;
};

/**
 * Get badge status for a product (top rated, popular, new)
 * Queries the product_badges view directly using product_id
 * Falls back gracefully if view doesn't exist
 */
const getProductBadge = async (productId: string) => {
  const { data, error } = await supabase
    .from('product_badges')
    .select('product_id, is_top_rated, is_popular, is_new')
    .eq('product_id', productId)
    .maybeSingle();

  if (error) {
    // Silently handle missing view (PGRST205) - view may not exist yet
    if (error.code === 'PGRST205' || error.code === '42P01') {
      // Only log once per session
      if (!sessionStorage.getItem('views_warning_shown')) {
        console.warn('⚠️ Database views not found - See DATABASE_SETUP.md for instructions');
        sessionStorage.setItem('views_warning_shown', 'true');
      }
      return null;
    }
    console.error('Error fetching product badge:', error);
    return null;
  }
  // Map to expected format for backward compatibility
  return data ? {
    product_id: data.product_id,
    qualifies_for_badge: data.is_top_rated,
    is_top_rated: data.is_top_rated,
    is_popular: data.is_popular,
    is_new: data.is_new,
  } : null;
};

/**
 * Enrich products with stats and badges
 * Manually fetches and attaches view data
 */
const enrichProductsWithStats = async (products: Product[]): Promise<ProductWithStats[]> => {
  const enriched = await Promise.all(
    products.map(async (product) => {
      const [stats, badge] = await Promise.all([
        getProductStats(product.id),
        getProductBadge(product.id),
      ]);

      return {
        ...product,
        rating_stats: stats ? [stats] : [],
        badge: badge ? [badge] : [],
      } as ProductWithStats;
    })
  );

  return enriched;
};

/**
 * Get all active products with stats
 */
export const getProducts = async (filters?: {
  form?: string;
  search?: string;
  active?: boolean;
}): Promise<ProductWithStats[]> => {
  // 🔧 DEVELOPMENT MODE - Return mock products
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Returning mock products');
    const mockProducts: ProductWithStats[] = [
      {
        id: 'product-1',
        sku: 'MWL-001',
        product_name: 'Blue Dream',
        brand: 'Premium Cannabis Co',
        form: 'flower',
        thc_pct: 18.5,
        cbd_pct: 0.5,
        pack_size: '3.5g',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        rating_stats: [{
          product_id: 'product-1',
          review_count: 24,
          avg_stars: 4.5,
        }],
        badge: [{
          product_id: 'product-1',
          qualifies_for_badge: true,
        }],
      },
      {
        id: 'product-2',
        sku: 'MWL-002',
        product_name: 'Northern Lights',
        brand: 'Green Leaf Co',
        form: 'flower',
        thc_pct: 16.0,
        cbd_pct: 1.0,
        pack_size: '3.5g',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        rating_stats: [{
          product_id: 'product-2',
          review_count: 18,
          avg_stars: 4.2,
        }],
        badge: [],
      },
      {
        id: 'product-3',
        sku: 'MWL-003',
        product_name: 'CBD Oil 20mg/ml',
        brand: 'Medical Cannabis Ltd',
        form: 'oil',
        thc_pct: 1.0,
        cbd_pct: 20.0,
        pack_size: '30ml',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        rating_stats: [{
          product_id: 'product-3',
          review_count: 32,
          avg_stars: 4.8,
        }],
        badge: [{
          product_id: 'product-3',
          qualifies_for_badge: true,
        }],
      },
      {
        id: 'product-4',
        sku: 'MWL-004',
        product_name: 'Sour Diesel',
        brand: 'Premium Cannabis Co',
        form: 'flower',
        thc_pct: 22.0,
        cbd_pct: 0.2,
        pack_size: '3.5g',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        rating_stats: [{
          product_id: 'product-4',
          review_count: 15,
          avg_stars: 3.8,
        }],
        badge: [],
      },
      {
        id: 'product-5',
        sku: 'MWL-005',
        product_name: 'Purple Kush',
        brand: 'Green Leaf Co',
        form: 'flower',
        thc_pct: 19.5,
        cbd_pct: 0.8,
        pack_size: '3.5g',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        rating_stats: [{
          product_id: 'product-5',
          review_count: 27,
          avg_stars: 4.6,
        }],
        badge: [{
          product_id: 'product-5',
          qualifies_for_badge: true,
        }],
      },
      {
        id: 'product-6',
        sku: 'MWL-006',
        product_name: 'CBD Capsules 25mg',
        brand: 'Medical Cannabis Ltd',
        form: 'capsule',
        thc_pct: 0.5,
        cbd_pct: 25.0,
        pack_size: '30 caps',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        rating_stats: [{
          product_id: 'product-6',
          review_count: 12,
          avg_stars: 4.1,
        }],
        badge: [],
      },
    ];

    // Apply filters
    let filtered = mockProducts;
    
    if (filters?.active === false) {
      filtered = [];
    }
    
    if (filters?.form) {
      filtered = filtered.filter(p => p.form === filters.form);
    }
    
    if (filters?.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(p => 
        p.product_name.toLowerCase().includes(search) ||
        p.brand?.toLowerCase().includes(search) ||
        p.sku.toLowerCase().includes(search)
      );
    }

    console.log(`✅ Returning ${filtered.length} mock products`);
    return filtered;
  }
  
  let query = supabase.from('products').select('*');

  // Apply filters
  if (filters?.active !== undefined) {
    query = query.eq('active', filters.active);
  } else {
    query = query.eq('active', true); // Default to active only
  }

  if (filters?.form) {
    query = query.eq('form', filters.form);
  }

  if (filters?.search) {
    query = query.or(`product_name.ilike.%${filters.search}%,brand.ilike.%${filters.search}%,sku.ilike.%${filters.search}%`);
  }

  const { data, error } = await query.order('product_name');

  if (error) throw error;
  if (!data || data.length === 0) return [];

  // Manually enrich with stats
  return enrichProductsWithStats(data);
};

/**
 * Get single product by ID with all relations
 */
export const getProductById = async (id: string): Promise<ProductWithStats | null> => {
  // 🔧 DEVELOPMENT MODE - Return mock product
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Returning mock product for ID:', id);
    
    const mockProductsMap: Record<string, ProductWithStats> = {
      'product-1': {
        id: 'product-1',
        sku: 'MWL-001',
        product_name: 'Blue Dream',
        brand: 'Premium Cannabis Co',
        form: 'flower',
        thc_pct: 18.5,
        cbd_pct: 0.5,
        pack_size: '3.5g',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        rating_stats: [{
          product_id: 'product-1',
          review_count: 24,
          avg_stars: 4.5,
        }],
        badge: [{
          product_id: 'product-1',
          qualifies_for_badge: true,
        }],
      },
      'product-2': {
        id: 'product-2',
        sku: 'MWL-002',
        product_name: 'Northern Lights',
        brand: 'Green Leaf Co',
        form: 'flower',
        thc_pct: 16.0,
        cbd_pct: 1.0,
        pack_size: '3.5g',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        rating_stats: [{
          product_id: 'product-2',
          review_count: 18,
          avg_stars: 4.2,
        }],
        badge: [],
      },
      'product-3': {
        id: 'product-3',
        sku: 'MWL-003',
        product_name: 'CBD Oil 20mg/ml',
        brand: 'Medical Cannabis Ltd',
        form: 'oil',
        thc_pct: 1.0,
        cbd_pct: 20.0,
        pack_size: '30ml',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        rating_stats: [{
          product_id: 'product-3',
          review_count: 32,
          avg_stars: 4.8,
        }],
        badge: [{
          product_id: 'product-3',
          qualifies_for_badge: true,
        }],
      },
      'product-4': {
        id: 'product-4',
        sku: 'MWL-004',
        product_name: 'Sour Diesel',
        brand: 'Premium Cannabis Co',
        form: 'flower',
        thc_pct: 22.0,
        cbd_pct: 0.2,
        pack_size: '3.5g',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        rating_stats: [{
          product_id: 'product-4',
          review_count: 15,
          avg_stars: 3.8,
        }],
        badge: [],
      },
      'product-5': {
        id: 'product-5',
        sku: 'MWL-005',
        product_name: 'Purple Kush',
        brand: 'Green Leaf Co',
        form: 'flower',
        thc_pct: 19.5,
        cbd_pct: 0.8,
        pack_size: '3.5g',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        rating_stats: [{
          product_id: 'product-5',
          review_count: 27,
          avg_stars: 4.6,
        }],
        badge: [{
          product_id: 'product-5',
          qualifies_for_badge: true,
        }],
      },
      'product-6': {
        id: 'product-6',
        sku: 'MWL-006',
        product_name: 'CBD Capsules 25mg',
        brand: 'Medical Cannabis Ltd',
        form: 'capsule',
        thc_pct: 0.5,
        cbd_pct: 25.0,
        pack_size: '30 caps',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        rating_stats: [{
          product_id: 'product-6',
          review_count: 12,
          avg_stars: 4.1,
        }],
        badge: [],
      },
    };
    
    const product = mockProductsMap[id];
    if (!product) {
      console.log('❌ Mock product not found for ID:', id);
      return null;
    }
    
    console.log('✅ Returning mock product:', product.product_name);
    return product;
  }
  
  // First, get the product
  const { data: product, error: productError } = await supabase
    .from('products')
    .select('*')
    .eq('id', id)
    .single();

  if (productError) {
    if (productError.code === 'PGRST116') return null; // Not found
    throw productError;
  }

  // Then get batches (this is a real table with FK, so relationship works)
  const { data: batches, error: batchesError } = await supabase
    .from('batches')
    .select('*')
    .eq('product_id', id)
    .order('received_at', { ascending: false });

  if (batchesError) {
    console.error('Error fetching batches:', batchesError);
  }

  // Get stats and badge from views
  const [stats, badge] = await Promise.all([
    getProductStats(id),
    getProductBadge(id),
  ]);

  return {
    ...product,
    batches: batches || [],
    rating_stats: stats ? [stats] : [],
    badge: badge ? [badge] : [],
  } as ProductWithStats;
};

/**
 * Get product by SKU
 */
export const getProductBySku = async (sku: string): Promise<Product | null> => {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('sku', sku)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
};

/**
 * Create product (admin only)
 */
export const createProduct = async (product: ProductInsert): Promise<Product> => {
  const { data, error } = await supabase
    .from('products')
    .insert(product)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Update product (admin only)
 */
export const updateProduct = async (id: string, updates: ProductUpdate): Promise<Product> => {
  const { data, error } = await supabase
    .from('products')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Delete product (admin only)
 */
export const deleteProduct = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('products')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

/**
 * Get top rated products
 */
export const getTopRatedProducts = async (limit: number = 10): Promise<ProductWithStats[]> => {
  // First get all products
  const { data: products, error } = await supabase
    .from('products')
    .select('*')
    .eq('active', true)
    .order('product_name');

  if (error) throw error;
  if (!products || products.length === 0) return [];

  // Enrich with stats
  const enriched = await enrichProductsWithStats(products);

  // Sort by rating and limit
  return enriched
    .filter(p => p.rating_stats?.[0]?.avg_stars)
    .sort((a, b) => {
      const aStars = a.rating_stats?.[0]?.avg_stars || 0;
      const bStars = b.rating_stats?.[0]?.avg_stars || 0;
      return bStars - aStars;
    })
    .slice(0, limit);
};

/**
 * Get products by form type
 */
export const getProductsByForm = async (form: string): Promise<ProductWithStats[]> => {
  const { data: products, error } = await supabase
    .from('products')
    .select('*')
    .eq('active', true)
    .eq('form', form)
    .order('product_name');

  if (error) throw error;
  if (!products || products.length === 0) return [];

  return enrichProductsWithStats(products);
};

/**
 * Search products
 */
export const searchProducts = async (query: string): Promise<ProductWithStats[]> => {
  const { data: products, error } = await supabase
    .from('products')
    .select('*')
    .eq('active', true)
    .or(`product_name.ilike.%${query}%,brand.ilike.%${query}%,sku.ilike.%${query}%`)
    .order('product_name')
    .limit(20);

  if (error) throw error;
  if (!products || products.length === 0) return [];

  return enrichProductsWithStats(products);
};